import type { CSSProperties } from 'react';
import { differenceInCalendarDays, format, formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { LEAD_SOURCE_LABELS, type AlertReason, type CardDensity, type LeadSource } from '@troia-v3/types';
import type { IconName } from '@/shared/icons';
import type { KanbanLeadCard, PopulatedLead } from '../types';
import { CHANNEL_ICONS, CHANNEL_LABELS, FLAME_COLORS, narrowTemperature } from './lead-visuals';
import { getLeadInitials } from './leadRowHelpers';

/**
 * Fonte de dados do card. A intersecção existe porque `nextFollowUpAt` e `customer`
 * chegam apenas no card do kanban (tarefa 10) e o componente aceita as duas formas
 * (`PopulatedLead` do dashboard e `KanbanLeadCard` do board). Quando o
 * `@troia-v3/types` publicado já trouxer os dois campos em `LeadKanbanCard`, a
 * intersecção vira redundante — não quebra e pode ser removida.
 */
export type LeadCardSource = (PopulatedLead | KanbanLeadCard) & {
  nextFollowUpAt?: string;
  customer?: { id: string; name: string };
};

/** Limites resolvidos da etapa (board → tarefa 10). Mesma forma do `AlertClockConfig` sem `enabled`. */
export interface LeadStepThreshold {
  inactivity?: { warningDays: number; criticalDays: number };
  timeInStep?: { warningDays: number; criticalDays: number };
}

export interface LeadCardTextTone {
  label: string;
  /** Sempre uma CSS variable do tema — nunca hex. */
  color: string;
}

export interface LeadCardMeta extends LeadCardTextTone {
  icon: IconName;
  /** `Ganho` é o único meta em semibold (mockup `.tk-meta` won). */
  strong: boolean;
}

export interface LeadCardAlertTooltip {
  title: string;
  /** Hex fixo: o tooltip do DS é roxo fixo (F80) — token de tema aqui seria ilegível. */
  titleColor: string;
  body: string;
  hint?: string;
}

export interface LeadCardViewModel {
  id: string;
  contactName: string;
  contactPicture?: string;
  assigneeName?: string;
  assigneeFirstName?: string;
  assigneePicture?: string;
  /** Empresa do card de Detalhes — `contact.customerId → customer.name` (spec §10). */
  customerName?: string;
  /** Sub do card Normal: descrição → empresa → canal. */
  subtitle?: string;
  /** "R$ 18.500" (sem centavos) — Detalhes e Normal. */
  budgetLabel?: string;
  /** "R$ 31,2k" — Compacto. */
  budgetCompact?: string;
  temperature?: 'hot' | 'warm' | 'cold';
  /** "Origem · Canal": "Meta · WhatsApp". */
  originChannelLabel: string;
  channelIcon: IconName;
  tags: string[];
  /** Respostas do formulário de captura — só chegam com o filtro "Formulário" ativo (mockup 12). */
  formChips: string[];
  followUp: LeadCardTextTone;
  stagnation: LeadCardTextTone;
  meta: LeadCardMeta;
  /** Barra de acento à esquerda (inset). `undefined` = card sem acento. */
  accentColor?: string;
  /** Ponto de 6px do Compacto. */
  dotColor: string;
  alertTooltip: LeadCardAlertTooltip | null;
}

const currency = new Intl.NumberFormat('pt-BR', {
  style: 'currency', currency: 'BRL', minimumFractionDigits: 0, maximumFractionDigits: 0,
});

/** "R$ 18.500" — o mockup não mostra centavos em nenhum dos três cards. */
export const formatCardCurrency = (value?: number): string | undefined =>
  value ? currency.format(value) : undefined;

/** "R$ 31,2k" / "R$ 1,4M" — valor da `.crm1-line__value` do Compacto. */
export const formatCardCurrencyCompact = (value?: number): string | undefined => {
  if (!value) return undefined;
  if (value >= 1_000_000) return `R$ ${(value / 1_000_000).toFixed(1).replace('.', ',')}M`;
  if (value >= 1_000) return `R$ ${(value / 1_000).toFixed(1).replace('.', ',')}k`;
  return `R$ ${value.toFixed(0)}`;
};

const formatRelative = (iso?: string): string => {
  if (!iso) return 'Sem data';
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true, locale: ptBR });
  } catch {
    return 'Data inválida';
  }
};

const formatShortDate = (iso?: string): string => {
  if (!iso) return '';
  try { return format(new Date(iso), 'dd/MM'); } catch { return ''; }
};

const formatFullDate = (iso?: string): string => {
  if (!iso) return '';
  try { return format(new Date(iso), 'dd/MM/yyyy'); } catch { return ''; }
};

/** Relógio governante: `alertReason.days` quando presente, senão o maior dos dois contadores. */
const getAlertDays = (lead: LeadCardSource): number | undefined => {
  if (typeof lead.alertReason?.days === 'number') return lead.alertReason.days;
  const values = [lead.daysSinceActivity, lead.daysInStep].filter(
    (v): v is number => typeof v === 'number',
  );
  return values.length ? Math.max(...values) : undefined;
};

const buildReasonBody = (reason: AlertReason): string => {
  switch (reason.clock) {
    case 'no_activities': return 'Sem atividades registradas';
    case 'overdue': return 'Atividade atrasada';
    case 'inactivity':
      return `${reason.days ?? '-'} dias sem atividade · limite ${reason.thresholdDays ?? '-'}` +
        (reason.source === 'step' ? ' (etapa)' : ' (funil)');
    case 'timeInStep':
      return `${reason.days ?? '-'} dias na etapa · limite ${reason.thresholdDays ?? '-'}`;
    default: return 'Alerta ativo.';
  }
};

const buildAlertTooltip = (lead: LeadCardSource): LeadCardAlertTooltip | null => {
  const level = lead.alertLevel;
  if (!level) return null;
  if (level === 'snoozed') {
    return {
      title: 'Silenciado',
      titleColor: '#cbd5e1',
      body: lead.alertSnoozedUntil
        ? `Alertas pausados até ${formatFullDate(lead.alertSnoozedUntil)}.`
        : 'Alertas pausados.',
    };
  }
  if (level === 'ok') {
    return { title: 'Em dia', titleColor: '#6ee7b7', body: 'Sem alertas ativos no momento.' };
  }
  if (!lead.alertReason) return null;
  return {
    title: level === 'critical' ? 'Crítico' : 'Alerta',
    titleColor: level === 'critical' ? '#fca5a5' : '#fcd34d',
    body: buildReasonBody(lead.alertReason),
    hint: 'Registre uma atividade para voltar ao verde.',
  };
};

/** Campo "Follow-up" do card de Detalhes — vem de `nextFollowUpAt` (tarefa 10). */
const buildFollowUp = (iso?: string): LeadCardTextTone => {
  if (!iso) return { label: 'Não agendado', color: 'var(--theme-muted-text)' };
  const days = differenceInCalendarDays(new Date(iso), new Date());
  if (days < 0) return { label: `Atrasado ${Math.abs(days)}d`, color: 'var(--theme-error-fg)' };
  if (days === 0) return { label: 'Hoje', color: 'var(--theme-warning-fg)' };
  if (days === 1) return { label: 'Amanhã', color: 'var(--theme-card-text)' };
  return { label: `Em ${days}d`, color: 'var(--theme-card-text)' };
};

/**
 * Campo "Parado": "9d · limite 5d" — e o limite aparece MESMO com o lead saudável
 * (spec §8). O motor de alerta só devolve `thresholdDays` em warning/critical, por
 * isso a queda para o mapa `stepThresholds` do board. "Limite" é sempre o
 * `warningDays` (o primeiro limiar cruzado); `criticalDays` já está dito pela cor.
 */
const buildStagnation = (lead: LeadCardSource, threshold?: LeadStepThreshold): LeadCardTextTone => {
  const days = getAlertDays(lead);
  const color =
    lead.alertLevel === 'critical' ? 'var(--theme-error-fg)'
    : lead.alertLevel === 'warning' ? 'var(--theme-warning-fg)'
    : 'var(--theme-card-text)';
  if (days === undefined) return { label: 'Sem registro', color: 'var(--theme-muted-text)' };
  const clock = lead.alertReason?.clock === 'timeInStep' ? 'timeInStep' : 'inactivity';
  const limit = lead.alertReason?.thresholdDays ?? threshold?.[clock]?.warningDays;
  return { label: limit ? `${days}d · limite ${limit}d` : `${days}d`, color };
};

/**
 * `.tk-meta` do rodapé do card Normal. Prioridade: Ganho → "parado Xd" →
 * silenciado → tempo relativo.
 */
const buildMeta = (lead: LeadCardSource): LeadCardMeta => {
  if (lead.businessStatus === 'won') {
    return { icon: 'check', label: 'Ganho', color: 'var(--theme-success-fg)', strong: true };
  }
  if (lead.alertLevel === 'critical' || lead.alertLevel === 'warning') {
    const days = getAlertDays(lead);
    if (days !== undefined) {
      return {
        icon: 'clock',
        label: `parado ${days}d`,
        color: lead.alertLevel === 'critical' ? 'var(--theme-error-fg)' : 'var(--theme-warning-fg)',
        strong: false,
      };
    }
  }
  if (lead.alertLevel === 'snoozed') {
    return {
      icon: 'bell-off',
      label: lead.alertSnoozedUntil
        ? `silenciado até ${formatShortDate(lead.alertSnoozedUntil)}`
        : 'silenciado',
      color: 'var(--theme-muted-text)',
      strong: false,
    };
  }
  return {
    icon: 'clock',
    label: formatRelative(lead.lastInteractionAt || lead.createdAt),
    color: 'var(--theme-muted-text)',
    strong: false,
  };
};

/** Ponto de 6px do Compacto (`.c3-dot--error/warning/muted`). */
const buildDotColor = (lead: LeadCardSource): string => {
  if (lead.alertLevel === 'critical') return 'var(--theme-error-color)';
  if (lead.alertLevel === 'warning') return 'var(--theme-warning-color)';
  if (lead.activityStatus === 'no_activities') return 'var(--theme-error-color)';
  if (lead.activityStatus === 'overdue') return 'var(--theme-warning-color)';
  return 'var(--theme-muted-text)';
};

/** Acento lateral (inset 2px). Só error/warning ganham barra — nunca decorativo. */
const buildAccentColor = (lead: LeadCardSource): string | undefined => {
  if (lead.alertLevel === 'critical') return 'var(--theme-error-color)';
  if (lead.alertLevel === 'warning') return 'var(--theme-warning-color)';
  if (lead.activityStatus === 'no_activities') return 'var(--theme-error-color)';
  if (lead.activityStatus === 'overdue') return 'var(--theme-warning-color)';
  return undefined;
};

export function buildLeadCardViewModel(
  lead: LeadCardSource,
  threshold?: LeadStepThreshold,
): LeadCardViewModel {
  const sourceLabel = lead.source
    ? (LEAD_SOURCE_LABELS[lead.source as LeadSource] ?? lead.source)
    : undefined;
  const channelLabel = lead.channel ? CHANNEL_LABELS[lead.channel] : undefined;
  const assigneeName = lead.assignee?.name;

  return {
    id: lead.id,
    contactName: lead.contact?.name || 'Sem nome',
    ...(lead.contact?.picture ? { contactPicture: lead.contact.picture } : {}),
    ...(assigneeName ? { assigneeName, assigneeFirstName: assigneeName.trim().split(/\s+/)[0] } : {}),
    ...(lead.assignee?.picture ? { assigneePicture: lead.assignee.picture } : {}),
    ...(lead.customer?.name ? { customerName: lead.customer.name } : {}),
    ...((lead.description || lead.customer?.name || channelLabel)
      ? { subtitle: lead.description || lead.customer?.name || channelLabel }
      : {}),
    ...(formatCardCurrency(lead.budget) ? { budgetLabel: formatCardCurrency(lead.budget) } : {}),
    ...(formatCardCurrencyCompact(lead.budget) ? { budgetCompact: formatCardCurrencyCompact(lead.budget) } : {}),
    // Flame some quando o lead é Ganho (mockup) — a mesma regra de hoje.
    ...(lead.businessStatus === 'won'
      ? {}
      : narrowTemperature(lead.temperature)
        ? { temperature: narrowTemperature(lead.temperature) }
        : {}),
    originChannelLabel: [sourceLabel, channelLabel].filter(Boolean).join(' · ') || '—',
    channelIcon: (lead.channel ? CHANNEL_ICONS[lead.channel] : undefined) ?? 'tag',
    tags: lead.contact?.tags ?? [],
    formChips: ('formAnswers' in lead && Array.isArray(lead.formAnswers))
      ? lead.formAnswers.map((answer) => answer.value).filter((value) => value !== '')
      : [],
    followUp: buildFollowUp(lead.nextFollowUpAt),
    stagnation: buildStagnation(lead, threshold),
    meta: buildMeta(lead),
    ...(buildAccentColor(lead) ? { accentColor: buildAccentColor(lead) } : {}),
    dotColor: buildDotColor(lead),
    alertTooltip: buildAlertTooltip(lead),
  };
}

export { getLeadInitials, FLAME_COLORS };

/**
 * Geometria do CONTÊINER por densidade — o que o mockup põe no `.c3-card` + na
 * classe da variante. Padding/direção/gap mudam por variante; borda, raio, fundo
 * e sombra são iguais nas três e ficam na casca.
 *   detalhes → `.c3-card` puro (col, gap 8, padding 14)
 *   normal   → `.crm1-two`  (grid 44px | 1fr, padding 0 — a calha sangra)
 *   compacto → `.crm1-line` (row, gap 4, padding 14)
 */
export const LEAD_CARD_SHELL: Record<CardDensity, CSSProperties> = {
  detailed: { display: 'flex', flexDirection: 'column', gap: 8, padding: 14 },
  normal: { display: 'grid', gridTemplateColumns: '44px minmax(0, 1fr)', alignItems: 'stretch', padding: 0 },
  compact: { display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 4, padding: 14 },
};

/**
 * Posição do kebab. Os mockups são estáticos e não desenham hover, então não há
 * kebab neles; a função é preservada seguindo o padrão `.tk-more` que o card já
 * usa (fundo sólido, invisível até o hover). No Compacto a linha tem 50px: o
 * kebab vai centralizado à direita, sobre o avatar do responsável, como já
 * acontece hoje sobre o conteúdo do rodapé.
 */
export const LEAD_CARD_KEBAB: Record<CardDensity, CSSProperties> = {
  detailed: { top: 9, right: 9 },
  normal: { top: 9, right: 9 },
  compact: { top: '50%', right: 6, transform: 'translateY(-50%)' },
};
